import { useMemo } from 'react';
import jwt_decode from 'jwt-decode';

/**
 * Hook to check if the current logged-in user possesses the `tenant_admin` or `super_admin` role.
 * Strictly inspects Keycloak client roles (`ATMOS-RESOURCE-SERVER`) and session user roles.
 */
export const useIsTenantAdmin = (): boolean => {
  return useMemo(() => {
    try {
      // 1. Check raw access token from storage
      const token =
        sessionStorage.getItem('authentication') ||
        sessionStorage.getItem('access_token') ||
        localStorage.getItem('access_token') ||
        localStorage.getItem('authentication');

      let decodedToken: any = null;
      if (token) {
        try {
          decodedToken = jwt_decode(token);
        } catch {
          // Fall back to sessionStorage.userDetails
        }
      }

      // 2. Check userDetails stored in sessionStorage (Standard in ATMOS)
      const userDetailsStr = sessionStorage.getItem('userDetails');
      let userDetails: any = null;
      if (userDetailsStr) {
        try {
          userDetails = JSON.parse(userDetailsStr);
        } catch {
          // Ignore parse errors
        }
      }

      // 3. Extract roles from Keycloak resource_access and user_roles
      const atmosRolesFromToken = decodedToken?.resource_access?.['ATMOS-RESOURCE-SERVER']?.roles || [];
      const userRolesFromToken = decodedToken?.user_roles || [];
      const atmosRolesFromDetails = userDetails?.resource_access?.['ATMOS-RESOURCE-SERVER']?.roles || [];
      const userRolesFromDetails = userDetails?.user_roles || [];
      const realmRoles = decodedToken?.realm_access?.roles || [];

      const allRoles = Array.from(new Set([
        ...(Array.isArray(atmosRolesFromToken) ? atmosRolesFromToken : []),
        ...(Array.isArray(userRolesFromToken) ? userRolesFromToken : []),
        ...(Array.isArray(atmosRolesFromDetails) ? atmosRolesFromDetails : []),
        ...(Array.isArray(userRolesFromDetails) ? userRolesFromDetails : []),
        ...(Array.isArray(realmRoles) ? realmRoles : []),
      ]));

      // 1. Super Admin possesses universal administrative authority (platform-wide role)
      if (allRoles.includes('super_admin')) {
        return true;
      }

      // 2. Strict multi-tenant verification: check role in the currently active tenant
      const currentTenant = decodedToken?.owner_org || userDetails?.owner_org;
      const rawOwnerOrgList = decodedToken?.ownerOrgList || userDetails?.ownerOrgList;

      if (currentTenant && rawOwnerOrgList) {
        try {
          const entries: Array<{ role: string; owner_org: string }> =
            typeof rawOwnerOrgList === 'string' ? JSON.parse(rawOwnerOrgList) : rawOwnerOrgList;

          if (Array.isArray(entries)) {
            const roleInCurrentTenant = entries
              .filter((e) => e.owner_org === currentTenant && allRoles.includes(e.role))
              .map((e) => e.role)
              .pop();

            // When current tenant entry is found, only tenant_admin is allowed; other roles return false
            return roleInCurrentTenant === 'tenant_admin';
          }
        } catch {
          return false;
        }
      }

      // 3. Fallback only if ownerOrgList claim is completely absent (e.g. mock / legacy dev tokens)
      return allRoles.includes('tenant_admin');
    } catch {
      return false;
    }
  }, []);
};

/**
 * Pure helper function matching contract specification section 2.1
 */
export function canLockUnlock(claims: any): boolean {
  if (!claims || typeof claims !== 'object') return false;
  const currentTenant = claims.owner_org;
  const roles = claims.resource_access?.['ATMOS-RESOURCE-SERVER']?.roles ?? [];
  if (roles.includes('super_admin')) return true;
  let entries: Array<{ role: string; owner_org: string }> = [];
  try {
    entries = typeof claims.ownerOrgList === 'string' ? JSON.parse(claims.ownerOrgList) : (claims.ownerOrgList ?? []);
  } catch {
    return false;
  }
  const roleHere = entries
    .filter((e) => e.owner_org === currentTenant && roles.includes(e.role))
    .map((e) => e.role)
    .pop();
  return roleHere === 'tenant_admin';
}

export default useIsTenantAdmin;
